import React, { useState } from "react";
import { Box, Grid, Stack, useTheme, FormControl, InputLabel, Select, MenuItem, Card, Typography, Chip, Avatar } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import BoltIcon from "@mui/icons-material/Bolt";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import WavesIcon from "@mui/icons-material/Waves";
import PeopleIcon from "@mui/icons-material/People";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import RecommendIcon from "@mui/icons-material/Recommend";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { StatCard, RadarChart, LineChart } from "../components/charts";
import { DashboardCard } from "../components/DashboardCard";
import { sampleTeams, teamProgressStats, userWellnessDimensions, teamAiInsights, recoveryTrends } from "../data";

export const TeamProgress: React.FC = () => {
  const theme = useTheme();
  const [selectedTeam, setSelectedTeam] = useState(sampleTeams[0].id);
  const team = sampleTeams.find((t) => t.id === selectedTeam) || sampleTeams[0];

  const statCards = [
    { metric: teamProgressStats.recovery_score, icon: FavoriteIcon },
    { metric: teamProgressStats.sleep_performance, icon: BedtimeIcon },
    { metric: teamProgressStats.day_strain, icon: BoltIcon },
    { metric: teamProgressStats.hrv, icon: MonitorHeartIcon },
    { metric: teamProgressStats.resting_heart_rate, icon: VolunteerActivismIcon },
    { metric: teamProgressStats.stress_score, icon: SelfImprovementIcon },
    { metric: teamProgressStats.sleep_efficiency, icon: WavesIcon },
    { metric: teamProgressStats.participation, icon: PeopleIcon },
  ];

  const insightIcons = { highlight: TipsAndUpdatesIcon, recommendation: RecommendIcon, watch: VisibilityIcon };
  const insightColors = { highlight: theme.palette.success.main, recommendation: theme.palette.info.main, watch: theme.palette.warning.main };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Select Department</InputLabel>
            <Select value={selectedTeam} label="Select Department" onChange={(e) => setSelectedTeam(e.target.value)}>
              {sampleTeams.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name} ({t.members} members)</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Chip label={`${team.members} members`} size="small" color="primary" variant="outlined" />
        </Box>

        <Grid container spacing={3}>
          {statCards.map((stat, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <StatCard metric={stat.metric} icon={stat.icon} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 5 }}>
            <DashboardCard title="Team Wellness Dimensions" subtitle="Department performance vs organisation benchmark.">
              <RadarChart
                data={userWellnessDimensions}
                seriesKeys={["value", "benchmark"]}
                seriesLabels={[team.name, "Organisation Avg"]}
                metricKey="metric"
                height={350}
                colors={[theme.palette.primary.main, theme.palette.text.disabled]}
              />
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <DashboardCard title="Team Recovery Trend" subtitle="Department recovery score over time.">
              <LineChart
                data={recoveryTrends}
                xAxisKey="label"
                series={[{ dataKey: "recovery_score", label: "Team Recovery", color: theme.palette.success.main, area: true }]}
                height={350}
                yAxisMin={0}
                yAxisMax={100}
              />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="AI Team Insights" subtitle="AI-generated observations and recommendations for this department.">
              <Stack spacing={2}>
                {teamAiInsights.map((insight, index) => {
                  const IconComp = insightIcons[insight.type];
                  const color = insightColors[insight.type];
                  return (
                    <Card key={index} variant="outlined" sx={{ p: 2, borderLeft: `4px solid ${color}` }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                        <Avatar sx={{ bgcolor: `${color}20`, width: 40, height: 40 }}>
                          <IconComp sx={{ color, fontSize: 20 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>{insight.title}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{insight.description}</Typography>
                        </Box>
                      </Box>
                    </Card>
                  );
                })}
              </Stack>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
